// LeaveTypeSection.tsx

import { TSCard } from '@/components/shared/card/Card';
import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { handleMutationResult } from '@/helpers/toastHelper';
import {
  CreateLeaveTypesMutation,
  UpdateLeaveTypesMutation,
  useCreateLeaveTypesMutation,
  useGetLeaveTypesQuery,
  useUpdateLeaveTypesMutation
} from '@/services/gql/graphql.generated';
import { createErrorToast } from '@/services/notification/ToastService';
import { FC, useEffect, useState } from 'react';
import strings from '../admin.translations';
import { LeaveTypeDeleteDialog } from './LeaveTypeDeleteDialog';
import { LeaveTypeEditCard } from './LeaveTypeEditCard';
import { LeaveTypeRow } from './leaveTypes.types';
import { LeaveTypesListCard } from './LeaveTypesListCard';

interface LeaveTypeEditorState {
  mode: 'create' | 'edit';
  initial: LeaveTypeRow;
  draft: LeaveTypeRow;
}

export const LeaveTypeSection: FC = () => {
  const [{ data: leaveTypesData, fetching: leaveTypesLoading }, refetchLeaveTypes] = useGetLeaveTypesQuery();
  const [{ fetching: creatingLeaveType }, createLeaveTypes] = useCreateLeaveTypesMutation();
  const [{ fetching: savingLeaveType }, updateLeaveTypes] = useUpdateLeaveTypesMutation();
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeRow[]>([]);
  const [editorState, setEditorState] = useState<LeaveTypeEditorState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LeaveTypeRow | null>(null);

  const isCreatingLeaveType = editorState?.mode === 'create';
  const editingLeaveType = editorState?.draft ?? null;

  const createEmptyLeaveType = (): LeaveTypeRow => ({
    id: '__new_leave_type__',
    name: { label: '' },
    displayName: { label: '' },
    color: '#ffffff',
    code: '',
    textColor: 'Black',
    active: true,
    managerOnly: false,
    rawProperties: {}
  });

  const normalizeLeaveType = (leaveType: LeaveTypeRow) => ({
    id: leaveType.id,
    name: leaveType.name.label.trim(),
    displayName: leaveType.displayName.label.trim(),
    color: leaveType.color,
    code: leaveType.code,
    textColor: leaveType.textColor,
    active: leaveType.active,
    managerOnly: Boolean(leaveType.managerOnly)
  });

  useEffect(() => {
    const rows =
      leaveTypesData?.leaveTypes
        ?.filter((leaveType): leaveType is NonNullable<typeof leaveType> => Boolean(leaveType?.id))
        .map(leaveType => {
          let rawProperties: Record<string, unknown> = {};

          if (leaveType.properties && leaveType.properties.trim().length > 0) {
            try {
              rawProperties = JSON.parse(leaveType.properties) as Record<string, unknown>;
            } catch {
              rawProperties = {};
            }
          }

          return {
            id: leaveType.id,
            name: { label: leaveType.name },
            displayName: {
              label:
                typeof rawProperties.displayName === 'string' && rawProperties.displayName
                  ? rawProperties.displayName
                  : leaveType.name
            },
            color: typeof rawProperties.color === 'string' && rawProperties.color ? rawProperties.color : '#ffffff',
            code: typeof rawProperties.systemCode === 'string' ? rawProperties.systemCode : '',
            textColor: rawProperties.textColor === 'White' ? 'White' : 'Black',
            active: leaveType.active,
            managerOnly: Boolean(rawProperties.managerOnly),
            rawProperties
          } satisfies LeaveTypeRow;
        }) ?? [];

    setLeaveTypes(rows);
  }, [leaveTypesData]);

  const hasUnsavedChanges =
    editorState !== null &&
    JSON.stringify(normalizeLeaveType(editorState.initial)) !== JSON.stringify(normalizeLeaveType(editorState.draft));

  const closeEditor = () => {
    setEditorState(null);
    setDiscardModalOpen(false);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setDiscardModalOpen(true);
      return;
    }

    closeEditor();
  };

  const handleSave = async () => {
    if (!editingLeaveType) {
      return;
    }

    const trimmedName = editingLeaveType.name.label.trim();
    const trimmedDisplayName = editingLeaveType.displayName.label.trim() || trimmedName;

    if (!trimmedName) {
      createErrorToast(strings.common.mandatory(strings.leaveTypeTable.name));
      return;
    }

    const properties = {
      ...editingLeaveType.rawProperties,
      displayName: trimmedDisplayName,
      color: editingLeaveType.color,
      systemCode: editingLeaveType.code,
      textColor: editingLeaveType.textColor,
      managerOnly: Boolean(editingLeaveType.managerOnly)
    };

    const result = isCreatingLeaveType
      ? await createLeaveTypes({
          leaveTypes: [
            {
              name: trimmedName,
              active: editingLeaveType.active,
              properties: JSON.stringify(properties)
            }
          ]
        })
      : await updateLeaveTypes({
          leaveTypes: [
            {
              id: editingLeaveType.id,
              name: trimmedName,
              active: editingLeaveType.active,
              properties: JSON.stringify(properties)
            }
          ]
        });

    await handleMutationResult(result, {
      successMessage: isCreatingLeaveType ? strings.editLeaveType.createSuccess : strings.editLeaveType.saveSuccess,
      errorMessage: isCreatingLeaveType ? strings.editLeaveType.createError : strings.editLeaveType.saveError,
      customSuccessCheck: data =>
        isCreatingLeaveType
          ? (data as CreateLeaveTypesMutation)?.createLeaveTypes === true
          : (data as UpdateLeaveTypesMutation)?.updateLeaveTypes === true,
      onSuccess: async () => {
        closeEditor();
        await refetchLeaveTypes({ requestPolicy: 'network-only' });
      }
    });
  };

  const startCreate = () => {
    const emptyLeaveType = createEmptyLeaveType();
    setEditorState({ mode: 'create', initial: emptyLeaveType, draft: emptyLeaveType });
  };

  if (editingLeaveType) {
    return (
      <LeaveTypeEditCard
        leaveType={editingLeaveType}
        mode={editorState?.mode}
        onChange={updatedLeaveType =>
          setEditorState(current => (current ? { ...current, draft: updatedLeaveType } : current))
        }
        onCancel={handleCancel}
        onSave={handleSave}
        onDelete={isCreatingLeaveType ? undefined : () => setDeleteTarget(editingLeaveType)}
        saving={isCreatingLeaveType ? creatingLeaveType : savingLeaveType}
      />
    );
  }

  if (leaveTypesLoading) {
    return (
      <TSCard className="p-6 min-h-[240px] flex items-center justify-center">
        <p className="text-secondary-std">{strings.state.loadingLeaveTypes}</p>
      </TSCard>
    );
  }

  return (
    <>
      <LeaveTypesListCard
        leaveTypes={leaveTypes}
        onEdit={leaveTypeId => {
          const leaveType = leaveTypes.find(item => item.id === leaveTypeId);
          if (!leaveType) return;
          setEditorState({ mode: 'edit', initial: leaveType, draft: leaveType });
        }}
        onAdd={startCreate}
      />
      <ConfirmDialog
        title={strings.editLeaveType.discardTitle}
        confirmLabel={strings.editLeaveType.discardConfirm}
        cancelLabel={strings.editLeaveType.cancel}
        open={discardModalOpen}
        onClose={() => setDiscardModalOpen(false)}
        onConfirm={closeEditor}
      >
        {strings.editLeaveType.discardMessage}
      </ConfirmDialog>
      <LeaveTypeDeleteDialog
        leaveType={deleteTarget}
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={async () => {
          setDeleteTarget(null);
          closeEditor();
          await refetchLeaveTypes({ requestPolicy: 'network-only' });
        }}
      />
    </>
  );
};
